// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// Node shape renderer for the SVG phylogenetic tree viewer.

import { el, rgba } from "./dom.js";
import { effectiveNodeSize } from "./node-scale.js";

// ---------------------------------------------------------------------------
// Internal geometry helpers
// ---------------------------------------------------------------------------

const PI = Math.PI;
const PI2 = PI * 2;
const PI_HALF = PI * 0.5;
const DEGREES_270 = PI * 1.5;

function regularPolygonPath(sides, x = 0, y = 0, size = 1) {
  const baseAngle = DEGREES_270;
  const step = PI2 / sides;
  const pts = [];
  for (let i = 0; i <= sides; i++) {
    pts.push([
      x + size * Math.cos(baseAngle + i * step),
      y + size * Math.sin(baseAngle + i * step),
    ]);
  }
  pts.push(pts[0]);
  return pts;
}

function starPolygonPath(spikes = 5, x = 0, y = 0, size = 1) {
  const outerRadius = size;
  const innerRadius = outerRadius * 0.5;
  const step = PI / spikes;
  const pts = [];
  let rot = (PI / 2) * 3;
  pts.push([x, y - outerRadius]);
  for (let i = 0; i < spikes; i++) {
    pts.push([x + Math.cos(rot) * outerRadius, y + Math.sin(rot) * outerRadius]);
    rot += step;
    pts.push([x + Math.cos(rot) * innerRadius, y + Math.sin(rot) * innerRadius]);
    rot += step;
  }
  pts.push([x, y - outerRadius]);
  return pts;
}

// ---------------------------------------------------------------------------
// Path d-string builder
// Supports null sentinels (move-to) matching the double-chevron definition.
// ---------------------------------------------------------------------------

function pathD(pts, cx, cy, size) {
  const parts = [];
  for (let i = 0; i < pts.length; i++) {
    const pt = pts[i];
    if (pt === null) {
      const next = pts[i + 1];
      parts.push(`M ${cx + size * next[0]},${cy + size * next[1]}`);
      i++; // skip the consumed next point
    } else {
      const px = cx + size * pt[0];
      const py = cy + size * pt[1];
      parts.push(i === 0 ? `M ${px},${py}` : `L ${px},${py}`);
    }
  }
  parts.push("Z");
  return parts.join(" ");
}

// ---------------------------------------------------------------------------
// Tiny shape descriptors
// Each descriptor is { type, build(cx, cy, radius) -> attrs object }
//   type: "circle" | "path"
//   For "path": build returns { d, transform? }
//   For "circle": build returns { cx, cy, r }
// ---------------------------------------------------------------------------

const SHAPES = {};

// Circle

SHAPES["circle"] = {
  type: "circle",
  build(cx, cy, r) {
    return { cx, cy, r };
  },
};

// Dot (1/8 radius circle)

SHAPES["dot"] = {
  type: "circle",
  build(cx, cy, r) {
    return { cx, cy, r: r / 8 };
  },
};

// Generic path shapes (no rotation)

function pathShape(ptsFactory) {
  return {
    type: "path",
    build(cx, cy, r) {
      return { d: pathD(ptsFactory(), cx, cy, r) };
    },
  };
}

// Generic rotated path shapes

function rotatedPathShape(ptsFactory, degrees) {
  return {
    type: "path",
    build(cx, cy, r) {
      return {
        d: pathD(ptsFactory(), cx, cy, r),
        transform: `rotate(${degrees},${cx},${cy})`,
      };
    },
  };
}

// Polygon-based shapes

SHAPES["diamond"]  = pathShape(() => regularPolygonPath(4));
SHAPES["hexagon"]  = pathShape(() => regularPolygonPath(6));
SHAPES["octagon"]  = pathShape(() => regularPolygonPath(8));
SHAPES["pentagon"] = pathShape(() => regularPolygonPath(5));
SHAPES["heptagon"] = pathShape(() => regularPolygonPath(7));
SHAPES["triangle"] = pathShape(() => regularPolygonPath(3));

SHAPES["heptagon-inverted"]  = rotatedPathShape(() => regularPolygonPath(7), 180);
SHAPES["pentagon-inverted"]  = rotatedPathShape(() => regularPolygonPath(5), 180);
SHAPES["triangle-inverted"]  = rotatedPathShape(() => regularPolygonPath(3), 180);
SHAPES["triangle-right"]     = rotatedPathShape(() => regularPolygonPath(3), 90);
SHAPES["triangle-left"]      = rotatedPathShape(() => regularPolygonPath(3), -90);

// Star / gram shapes

SHAPES["tetragram"]  = pathShape(() => starPolygonPath(4));
SHAPES["pentagram"]  = pathShape(() => starPolygonPath(5));
SHAPES["hexagram"]   = pathShape(() => starPolygonPath(6));
SHAPES["heptagram"]  = pathShape(() => starPolygonPath(7));
SHAPES["octagram"]   = pathShape(() => starPolygonPath(8));

SHAPES["pentagram-inverted"]  = rotatedPathShape(() => starPolygonPath(5), 180);
SHAPES["heptagram-inverted"]  = rotatedPathShape(() => starPolygonPath(7), 180);

// Alias: star = pentagram

SHAPES["star"]          = SHAPES["pentagram"];
SHAPES["star-inverted"] = SHAPES["pentagram-inverted"];

// Square

SHAPES["square"] = pathShape(() => [
  [-1, -1],
  [-1,  1],
  [ 1,  1],
  [ 1, -1],
  [-1, -1],
]);

// Plus

const PLUS_PTS = [
  [-(1/4), -1],
  [ (1/4), -1],
  [ (1/4), -(1/4)],
  [ 1,     -(1/4)],
  [ 1,      (1/4)],
  [ (1/4),  (1/4)],
  [ (1/4),  1],
  [-(1/4),  1],
  [-(1/4),  (1/4)],
  [-1,      (1/4)],
  [-1,     -(1/4)],
  [-(1/4), -(1/4)],
  [-(1/4), -1],
];

SHAPES["plus"]  = pathShape(() => PLUS_PTS);
SHAPES["cross"] = rotatedPathShape(() => PLUS_PTS, 45);

// Chevron

const CHEVRON_PTS = [
  [ 0, -1],
  [ 1,  0],
  [ 1,  1],
  [ 0,  0],
  [-1,  1],
  [-1,  0],
  [ 0, -1],
];

SHAPES["chevron"]          = pathShape(() => CHEVRON_PTS);
SHAPES["chevron-inverted"] = rotatedPathShape(() => CHEVRON_PTS, 180);
SHAPES["chevron-right"]    = rotatedPathShape(() => CHEVRON_PTS, 90);
SHAPES["chevron-left"]     = rotatedPathShape(() => CHEVRON_PTS, -90);

// Double chevron (contains null sentinel for the subpath break)

const DOUBLE_CHEVRON_PTS = [
  [ 0,  -1],
  [ 1,  -0.5],
  [ 1,   0],
  [ 0,  -0.5],
  [-1,   0],
  [-1,  -0.5],
  [ 0,  -1],
  null,
  [ 0,   0],
  [ 1,   0.5],
  [ 1,   1],
  [ 0,   0.5],
  [-1,   1],
  [-1,   0.5],
  [ 0,   0],
];

SHAPES["double-chevron"]          = pathShape(() => DOUBLE_CHEVRON_PTS);
SHAPES["double-chevron-inverted"] = rotatedPathShape(() => DOUBLE_CHEVRON_PTS, 180);
SHAPES["double-chevron-right"]    = rotatedPathShape(() => DOUBLE_CHEVRON_PTS, 90);
SHAPES["double-chevron-left"]     = rotatedPathShape(() => DOUBLE_CHEVRON_PTS, -90);

// Wye

SHAPES["wye"] = pathShape(() => [
  [ 0.366025,  0.211325],
  [ 0.366025,  0.943376],
  [-0.366025,  0.943376],
  [-0.366025,  0.211325],
  [-1,        -0.154701],
  [-0.633975, -0.788675],
  [ 0,        -0.42265],
  [ 0.633975, -0.788675],
  [ 1,        -0.154701],
]);

SHAPES["wye-inverted"] = rotatedPathShape(() => [
  [ 0.366025,  0.211325],
  [ 0.366025,  0.943376],
  [-0.366025,  0.943376],
  [-0.366025,  0.211325],
  [-1,        -0.154701],
  [-0.633975, -0.788675],
  [ 0,        -0.42265],
  [ 0.633975, -0.788675],
  [ 1,        -0.154701],
], 180);

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Build a single SVG shape element centered on (x, y) with the given radius.
 *
 * Stroke/stroke-width are set directly as attributes on the shape element
 * (no double-draw needed in SVG since stroke is painted over the fill in
 * the same z-layer).
 *
 * @param {string}  shapeId      - One of the Shapes enum string values.
 * @param {number}  x            - Center x in pixel space.
 * @param {number}  y            - Center y in pixel space.
 * @param {number}  radius       - Radius (half of nodeSize).
 * @param {string}  fillColour   - CSS colour string for the fill.
 * @param {string}  [strokeColour] - CSS colour string for the stroke border.
 * @param {number}  [strokeWidth=0] - Stroke width in pixels.
 * @returns {SVGElement}
 */
export function drawShape(shapeId, x, y, radius, fillColour, strokeColour, strokeWidth = 0) {
  const descriptor = SHAPES[shapeId];

  if (!descriptor) {
    // Unknown shape: fall back to a circle so the node is still visible.
    return el("circle", {
      cx: x,
      cy: y,
      r: radius,
      fill: fillColour,
      stroke: strokeWidth > 0 ? strokeColour : null,
      "stroke-width": strokeWidth > 0 ? strokeWidth : null,
    });
  }

  const hasBorder = strokeWidth > 0 && strokeColour;
  const commonAttrs = {
    fill: fillColour,
    stroke: hasBorder ? strokeColour : "none",
    "stroke-width": hasBorder ? strokeWidth : null,
    "stroke-linejoin": "round",
  };

  if (descriptor.type === "circle") {
    const { cx, cy, r } = descriptor.build(x, y, radius);
    return el("circle", { cx, cy, r, ...commonAttrs });
  }

  // Path shape
  const { d, transform } = descriptor.build(x, y, radius);
  return el("path", { d, transform: transform || null, ...commonAttrs });
}

/**
 * Render all visible leaf node shapes for the tree into a <g> element.
 *
 * Collapsed subtrees are skipped by advancing the index by (totalNodes - 1).
 *
 * @param {object} graph  - Graph object from buildGraph().
 * @param {object} props  - Props object (merged with defaultProps upstream).
 * @returns {SVGGElement}
 */
export function renderNodeShapes(graph, props) {
  const nodeRadius = effectiveNodeSize(props) * 0.5;

  let borderColour = null;
  let borderWidth = 0;

  if (props.showShapeBorders) {
    // shapeBorderColour is a [r,g,b,a] array stored under strokeColour in defaults.
    // The border alpha comes from shapeBorderAlpha (0–1 float applied to a/255).
    const rawColour = props.strokeColour ?? [34, 34, 34, 255];
    const alpha = props.shapeBorderAlpha ?? 0.28;

    // Compose a colour array with the border alpha baked in (0–255 scale).
    const [r, g, b] = rawColour;
    const a = Math.round(alpha * 255);
    borderColour = rgba([r, g, b, a], false);
    borderWidth = props.shapeBorderWidth ?? 1;
  }

  const group = el("g", { class: "node-shapes" });

  const traversal = graph.preorderTraversal;
  const first = graph.firstIndex;
  const last = graph.lastIndex;

  for (let i = first; i < last; i++) {
    const node = traversal[i];

    if (node.isCollapsed) {
      // Skip all descendants; the collapsed node itself is rendered below.
      // After this iteration i will advance by totalNodes-1 more via the loop,
      // giving a total skip of totalNodes (the node itself + its subtree).
      i += node.totalNodes - 1;

      // Collapsed nodes are typically drawn as a triangle/special marker by
      // other renderers; we skip them here unless they happen to be leaves
      // (which would be a degenerate tree).
      continue;
    }

    if (!node.isLeaf) continue;
    if (!node.shape)  continue;
    if (node.isHidden) continue;

    const shapeEl = drawShape(
      node.shape,
      node.x,
      node.y,
      nodeRadius,
      rgba(node.fillColour),
      borderColour,
      borderWidth,
    );

    // Tag for downstream hit-testing / selection.
    shapeEl.setAttribute("data-node-id", node.id);

    group.appendChild(shapeEl);
  }

  return group;
}
